import { useEffect, useRef } from 'react';
import { Box, Skeleton, Text } from '@nimbus-ds/components';
import { AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import { CATALOG_PRODUCT_HASHED_SLUG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import PredictMultiSelect from 'commons/components/PredictMultiSelect';
import { AiNavigationAttributes, useTranslationCommon } from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { useFetchTags } from 'domains/Catalog/Products/pages/hooks';
import { normalizeString } from 'domains/Catalog/Products/pages/hooks/useProductForm/useProductFormUtils';
import type { AIGeneratedType } from 'domains/Catalog/Products/productsSlice';
import type { TagsType, onChangeTagsType } from '../../TagsAndSeo';
import TagAndSeoItemTitle from '../TagAndSeoItemTitle';

interface AllTagsProps {
  tags: TagsType;
  aiGenerated: TagsType;
  disabled: boolean;
  missingNameAndDescription: boolean;
  onChange: onChangeTagsType;
  onClickGenerateWithAI: (type: AIGeneratedType) => void;
  aiNavigationAttributes?: AiNavigationAttributes;
  errors?: Partial<Record<string, string>>;
}

function AllTags({
  tags,
  onChange,
  aiGenerated,
  disabled,
  missingNameAndDescription,
  onClickGenerateWithAI,
  aiNavigationAttributes,
  errors,
}: AllTagsProps): JSX.Element {
  const t = useTranslationCatalog('products.detail.tags');
  const tCommon = useTranslationCommon();
  const tProduct = useTranslationCatalog();
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);
  const { optionsTags, isLoading, fetchStandardTags } = useFetchTags();
  const { requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const defaultValue = tags
    ? tags.map((tag) => ({
        label: tag,
        value: tag,
      }))
    : [];

  const handleChange = (values) => {
    const newValues = values.map(({ value }) => value);
    onChange({ name: 'tags', value: newValues });
  };
  const optionMessage = () => (isLoading ? t('loading') : t('withoutResults'));

  const getTooltipText = () => {
    if (tags.length > 0) return tCommon('aiGeneration.alreadyExists.tags');
    if (missingNameAndDescription)
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    return '';
  };

  const tooltipText = getTooltipText();

  const key = defaultValue.map((tag) => tag.value).join(',');

  const upsellTags = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_TAGS,
    callback: () => onClickGenerateWithAI('tags'),
    minValue: requiredTier,
    asAside: !hasCatalogProductHashedSlug,
  });

  const generateWithAiLink = (
    <GenerateWithAiLink
      onClick={upsellTags}
      fontSize="caption"
      trackingFeature={AiFeature.AI_PRODUCT_TAGS}
      disabled={!!tooltipText}
      tooltipText={tooltipText}
    />
  );

  const tagsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchStandardTags();
  }, [fetchStandardTags]);

  const tagsError = errors?.tags;
  const hasError = !!tagsError;

  // Find duplicate tags using the same normalization as validation
  const getDuplicateTags = (): string[] => {
    if (!tags || tags.length === 0 || !hasError) return [];

    const normalizedTags = new Map<string, string[]>();

    for (const tag of tags) {
      if (!tag) continue;
      const normalized = normalizeString(tag.trim());
      if (!normalizedTags.has(normalized)) {
        normalizedTags.set(normalized, []);
      }
      normalizedTags.get(normalized)?.push(tag);
    }

    const duplicates: string[] = [];
    normalizedTags.forEach((tagArray) => {
      if (tagArray.length > 1) {
        duplicates.push(...tagArray);
      }
    });

    return duplicates;
  };

  const duplicateTags = getDuplicateTags();

  useEffect(() => {
    if (tagsContainerRef.current) {
      if (hasError) {
        tagsContainerRef.current.setAttribute(
          'data-block-submit-error',
          'true',
        );
      } else {
        tagsContainerRef.current.removeAttribute('data-block-submit-error');
      }
    }
  }, [hasError]);

  return (
    <TagAndSeoItemTitle
      title={t('subTitle')}
      slot={generateWithAiLink}
      description={t('description')}
      aiNavigationAttributes={aiNavigationAttributes}
    >
      <Box
        ref={tagsContainerRef}
        className={hasError ? 'nimbus--input-validation--error' : undefined}
      >
        <Box display="flex" flexDirection="column" alignItems="stretch">
          {disabled ? (
            <Box width="100%">
              <Skeleton width="100%" height="40px" borderRadius="8px" />
            </Box>
          ) : (
            <>
              <PredictMultiSelect
                key={key}
                defaultValue={defaultValue}
                options={optionsTags}
                onChange={handleChange}
                optionMessage={optionMessage}
                createText={t('addTags')}
                aiGenerated={aiGenerated}
                disabled={false}
                hasError={hasError}
                duplicateTags={duplicateTags}
              />
              {hasError && !!tagsError && (
                <Text color="danger-textLow" fontSize="caption">
                  <br />
                  {tProduct(tagsError)}
                </Text>
              )}
            </>
          )}
        </Box>
      </Box>
    </TagAndSeoItemTitle>
  );
}

export default AllTags;
